import { faCalendarDays, faClock, faCommentLines, faLayerGroup } from "@fortawesome/pro-solid-svg-icons"

import { Box, Button, CardContent, Grid, Typography, useMediaQuery } from "@mui/material"
import { useTheme } from "@mui/styles"
import { ChipTag } from "components/ChipTag/ChipTag"
import LinkPaperBox from "components/LinkPaperBox"
import { allRoutes } from "generated/routes"
import { useTranslation } from "react-i18next"
import { NavLink } from "react-router-dom"
import { StyledBox, StyledFontAwesomeIcon, StyledGrid } from "./ExaminationInfoCardVertical.styled"
import {
  getColors,
  getExaminationColor,
  getExaminationIcon,
  getExaminationText
} from "shared/learning/components/helpers/examinationHelpers"
import moment from "moment"

const ExaminationInfoCardVertical = (props) => {
  const { item, assignedCourses } = props
  const { t } = useTranslation()
  const theme = useTheme()

  const examinationText = getExaminationText(item.examinationSummary, t)
  const examinationColor = getExaminationColor(item.examinationSummary)
  const examinationIcon = getExaminationIcon(item.examinationSummary)
  const { textColor, bgColor } = getColors(examinationColor, theme)

  const styleProps = {
    image: "/assets/learning/card_".concat(item.examinationType).concat(".jpg"),
    borderTopLeftRadius: "16px !important",
    borderTopRightRadius: "16px !important"
  }

  const completedRaw = item.examinationCertificate?.completedDate
  const startRaw = item.recertificationStartDate ?? item.recertification?.startDate
  const endRaw = item.recertificationEndDate ?? item.recertification?.endDate

  const completed = moment(completedRaw)
  const today = moment().startOf("day")
  const start = moment(item?.recertificationStartDate).startOf("day")
  const end = moment(item?.recertificationEndDate).endOf("day")

  const isRecertActive = !!item?.recertificationIsActive

  const isRecertInProgress =
    isRecertActive && start.isValid() && end.isValid() && today.isBetween(start, end, "day", "[]")

  const isRecertScheduled = isRecertActive && start.isValid() && today.isBefore(start, "day")

  const passedWithinRecertWindow =
    isRecertActive &&
    completedRaw &&
    startRaw &&
    endRaw &&
    completed.isValid() &&
    start.isValid() &&
    end.isValid() &&
    completed.isBetween(start, end, "day", "[]")

  return (
    <LinkPaperBox
      m={0}
      p={0}
      sx={{ boxShadow: "0px 0px 16px 0px rgba(0, 0, 0, 0.10)", borderRadius: "12px" }}>
      <NavLink
        style={{ position: "relative" }}
        to={allRoutes.LearningUserExamsDetails(item.userExaminationId)}>
        <Box
          sx={{
            position: "absolute",
            top: 16,
            right: 16,
            zIndex: 1,
            display: "flex",
            flexDirection: "row",
            gap: 1,
            flexWrap: "wrap",
            justifyContent: "flex-end"
          }}>
          {item?.recertificationIsActive && item?.examinationSummary?.hasPassed ? (
            <>
              {isRecertInProgress && (
                <ChipTag
                  text={t("learning.titles.recertification_period", "Recertification period")}
                  textColor={theme.palette.sky[700]}
                  bgColor={theme.palette.sky[100]}
                  icon={faClock}
                />
              )}

              {isRecertScheduled && (
                <ChipTag
                  text={`${t(
                    "learning.titles.recertification_period_starts",
                    "Recertification period starts"
                  )} ${start.format("YYYY-MM-DD")}`}
                  textColor={theme.palette.sky[700]}
                  bgColor={theme.palette.sky[100]}
                  icon={faCalendarDays}
                />
              )}
            </>
          ) : (
            <>
              {isRecertInProgress && (
                <ChipTag
                  text={t("learning.titles.recertification_period", "Recertification period")}
                  textColor={theme.palette.sky[700]}
                  bgColor={theme.palette.sky[100]}
                  icon={faClock}
                />
              )}

              {isRecertScheduled && (
                <ChipTag
                  text={`${t(
                    "learning.titles.recertification_period_starts",
                    "Recertification period starts"
                  )} ${start.format("YYYY-MM-DD")}`}
                  textColor={theme.palette.sky[700]}
                  bgColor={theme.palette.sky[100]}
                  icon={faCalendarDays}
                />
              )}

              <ChipTag
                text={examinationText}
                textColor={textColor}
                bgColor={bgColor}
                icon={examinationIcon}
              />
            </>
          )}
        </Box>

        <StyledBox item xs={12} {...styleProps} />
        <CardContent sx={{ borderBottomRightRadius: 12, borderBottomLeftRadius: 12 }}>
          <Grid
            container
            alignItems={useMediaQuery(theme.breakpoints.down("xl")) && "flex-start"}
            flexDirection={useMediaQuery(theme.breakpoints.up("xl")) ? "row" : "column"}
            justifyContent="space-between"
            flexWrap="wrap">
            <Grid item>
              <Typography variant="h5">
                {assignedCourses ? assignedCourses?.examinationName : item.examinationName}
              </Typography>
            </Grid>
          </Grid>
          <Box>
            <Grid
              container
              direction="row"
              justifyContent="flex-start"
              alignItems="center"
              spacing={2}
              pt={2}
              pb={isRecertInProgress && !passedWithinRecertWindow ? 2 : 3}>
              <StyledGrid item>
                <StyledFontAwesomeIcon icon={faCommentLines} />
                <Typography variant="bodySmall">
                  {item.examinationQuestionCount} {t("learning.titles.questions", "questions")}
                </Typography>
              </StyledGrid>
              <StyledGrid item>
                <StyledFontAwesomeIcon icon={faLayerGroup} />
                <Typography variant="bodySmall">
                  {item.examinationAreaCount} {t("learning.titles.segments", "segments")}
                </Typography>
              </StyledGrid>
            </Grid>
            {isRecertInProgress && !passedWithinRecertWindow && (
              <Box display="flex" flexDirection="column" pb={3}>
                <Typography variant="bodySmall" fontWeight={600} mb={0.5}>
                  {t("learning.titles.recert_in_progress_long", "Recertification period in progress")}
                </Typography>

                <Typography variant="bodySmall" sx={{ color: "text.secondary" }}>
                  {t("globa.ongoing_between", "Ongoing between")}{" "}
                  <Box component="span">{moment(item?.recertificationStartDate).format("DD-MM-YYYY")}</Box> –{" "}
                  <Box component="span">{moment(item?.recertificationEndDate).format("DD-MM-YYYY")}</Box>.
                </Typography>
              </Box>
            )}
            <Grid item>
              <Button
                variant="contained"
                color="primary"
                component={NavLink}
                to={allRoutes.LearningUserExamsExamination(item.userExaminationId)}>
                {t("learning.admin.overview.buttons.startCourse", "Start course")}
              </Button>
            </Grid>
          </Box>
        </CardContent>
      </NavLink>
    </LinkPaperBox>
  )
}

export default ExaminationInfoCardVertical
